



// 



import { NgModule, ErrorHandler } from "@angular/core";
import { RouterModule } from "@angular/router";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule } from '@angular/common/http';

import 'bootstrap';
import { TranslateModule, TranslateLoader } from "@ngx-translate/core";
import { NgxDatatableModule } from '@swimlane/ngx-datatable';

import { ModalModule, BsModalService } from 'ngx-bootstrap/modal';
import { TooltipModule } from "ngx-bootstrap/tooltip";
import { PopoverModule } from "ngx-bootstrap/popover";
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { CarouselModule } from 'ngx-bootstrap/carousel';
import { ChartsModule } from 'ng2-charts';

import { AppRoutingModule } from './app-routing.module';
import { AppErrorHandler } from './app-error.handler';
import { AppTitleService } from './services/app-title.service';
import { AppTranslationService, TranslateLanguageLoader } from './services/app-translation.service';
import { ConfigurationService } from './services/configuration.service';
import { AlertService } from './services/alert.service';
import { LocalStoreManager } from './services/local-store-manager.service';
import { EndpointFactory } from './services/endpoint-factory.service';
import { NotificationService } from './services/notification.service';
import { NotificationEndpoint } from './services/notification-endpoint.service';
import { AccountService } from './services/account.service';
import { AccountEndpoint } from './services/account-endpoint.service';

import { EqualValidator } from './directives/equal-validator.directive';
import { LastElementDirective } from './directives/last-element.directive';
import { AutofocusDirective } from './directives/autofocus.directive';
import { BootstrapTabDirective } from './directives/bootstrap-tab.directive';
import { BootstrapToggleDirective } from './directives/bootstrap-toggle.directive';
import { BootstrapSelectDirective } from './directives/bootstrap-select.directive';
import { BootstrapDatepickerDirective } from './directives/bootstrap-datepicker.directive';
import { GroupByPipe } from './pipes/group-by.pipe';

import { AppComponent } from "./components/app.component";
import { LoginComponent } from "./components/login/login.component";
import { HomeComponent } from "./components/home/home.component";
import { ProductsComponent } from "./components/products/products.component";
import { SettingsComponent } from "./components/settings/settings.component";
import { NotFoundComponent } from "./components/not-found/not-found.component";

import { NotificationsViewerComponent } from "./components/controls/notifications-viewer.component";
import { SearchBoxComponent } from "./components/controls/search-box.component";
import { UserInfoComponent } from "./components/controls/user-info.component";
import { UserPreferencesComponent } from "./components/controls/user-preferences.component";
import { UsersManagementComponent } from "./components/controls/users-management.component";
import { RolesManagementComponent } from "./components/controls/roles-management.component";
import { RoleEditorComponent } from "./components/controls/role-editor.component";
import { GroupsComponent } from "./components/groups/groups.component";
import { ApiService } from "./services/api.service";
import { HttpModule } from "@angular/http";
import { BenefitsComponent } from "./components/benefits/benefits.component";

//material
import {
    MatCardModule,
    MatTooltipModule, MatIconModule, MatButtonModule, MatDialogModule, MatOptionModule, MatSelectModule, MatProgressSpinnerModule, MatListModule,
    MatInputModule, MatDatepickerModule, MatNativeDateModule, MatCheckboxModule, MatAutocompleteModule, MatBottomSheetModule, MatStepperModule,
    MatSlideToggleModule, MatTabsModule, MatMenuModule, MatExpansionModule, MatProgressBarModule, MatGridListModule, MatPaginatorModule, MatTableModule
} from '@angular/material';
import { CreateEditBenefitModalComponent } from "./components/benefits/create-edit-benefit-modal/create-edit-benefit-modal.component";
import { CreateEditVendorModalComponent } from "./components/vendors/create-edit-vendor-modal/create-edit-vendor-modal.component";
import { VendorsComponent } from "./components/vendors/vendors.component";
import { CreateEditProductModalComponent } from "./components/products/create-edit-product-modal/create-edit-product-modal.component";
import { CreateEditGroupModalComponent } from "./components/groups/create-edit-group-modal/create-edit-group-modal.component";
import { PageHeaderComponent } from "./components/page-header/page-header.component";
import { PaytypesComponent } from "./components/paytypes/paytypes.component";
import { CreateEditPaytypeModalComponent } from "./components/paytypes/create-edit-paytype-modal/create-edit-paytype-modal.component";
import { ProductDetailComponent } from "./components/products/product-detail/product-detail.component";
import { LinkBenefitModalComponent } from "./components/products/product-detail/link-benefit-modal/link-benefit-modal.component";
import { VendorProductLookupModalComponent } from "./components/vendors/vendor-product-lookup-modal/vendor-product-lookup-modal.component";
import { GroupProductModalComponent } from "./components/groups/group-product-modal/group-product-modal.component";
import { MembersComponent } from "./components/members/members.component";
import { MemberDetailDialogComponent } from "./components/members/member-detail-dialog/member-detail-dialog.component";
import { EditMemberSheetComponent } from "./components/members/edit-member-sheet/edit-member-sheet";
import { ChangePasswordSheetComponent } from "./components/members/change-password-sheet/change-password-sheet";
import { WhitelabelsComponent } from "./components/whitelabels/whitelabels.component";
import { CreateEditWhitelabelModalComponent } from "./components/whitelabels/create-edit-whitelabel-modal/create-edit-whitelabel-modal.component";
import { WhitelabelConfigsComponent } from "./components/whitelabels/whitelabel-configs/whitelabel-configs.component";
import { EditDefaultSheetComponent } from "./components/whitelabels/whitelabel-configs/edit-default/edit-default-sheet";
import { LearnMoreContentSheetComponent } from "./components/whitelabels/whitelabel-configs/learn-more-content/learn-more-content-sheet";
import { BenefitContentsComponent } from "./components/benefit-contents/benefit-contents.component";
import { BenefitIconSheetComponent } from "./components/benefit-contents/benefit-icon/benefit-icon-sheet";
import { HistoryComponent } from "./components/history/history.component";
import { LoggersComponent } from "./components/loggers/loggers.component";
import { SystemLogPipe } from "./pipes/system-log.pipe";
import { QuoteSettingsComponent } from "./components/quote-settings/quote-settings.component";
import { CreateEditServiceModalComponent } from "./components/quote-settings/services/create-edit-service-modal/create-edit-service-modal.component";
import { ServicesComponent } from "./components/quote-settings/services/services.component";
import { ContractTemplatesComponent } from "./components/quote-settings/contract-templates/contract-templates.component";
import { CreateEditContractTemplateModalComponent } from "./components/quote-settings/contract-templates/create-edit-contract-template-modal/create-edit-contract-template-modal.component";
import { QuoteHomeComponent } from "./components/quote-home/quote-home.component";
import { CreateEditQuoteModalComponent } from "./components/quote-home/create-edit-quote-modal/create-edit-quote-modal.component";
import { CreateEditQuoteTemplateModalComponent } from "./components/quote-settings/quote-templates/create-edit-quote-template-modal/create-edit-quote-template-modal.component";
import { QuoteTemplatesComponent } from "./components/quote-settings/quote-templates/quote-templates.component";
import { CreateEditOrgModalComponent } from "./components/quote-settings/orgs/create-edit-org-modal/create-edit-org-modal.component";
import { OrgsComponent } from "./components/quote-settings/orgs/orgs.component";
import { CreateEditCustomerModalComponent } from "./components/quote-settings/customers/create-edit-customer-modal/create-edit-customer-modal.component";
import { CustomersComponent } from "./components/quote-settings/customers/customers.component";
import { ViewQuoteModalComponent } from "./components/quote-home/view-quote-modal/view-quote-modal.component";
import { MenuItemComponent } from "./components/menu-item/menu-item.component";

import { NgxSpinnerModule } from "ngx-spinner";
import { FilterPipe } from "./pipes/filter.pipe";
import { MenuItemContractComponent } from "./components/menu-item/menu-item-contract.component";
import { DragDropDirective } from "./directives/drag-drop.directive";
import { NewMemberComponent } from "./components/members/new-member/new-member.component";
import { CasesComponent } from "./components/cases-management/cases-management.component";
import { CaseDetailComponent } from "./components/cases-management/case-detail/case-detail.component";
import { NewCaseComponent } from "./components/cases-management/new-case/new-case.component";
import { SnotifyModule, SnotifyService, ToastDefaults } from "ng-snotify";
import { DependentService } from "./services/dependent.service";
import { DependentEventlogDialogComponent } from "./components/members/dependent-eventlog-dialog/dependent-eventlog-dialog.component";
import { BookmarkTemplatesComponent } from "./components/bookmark-templates/bookmark-templates.component";
import { BookmarkTemplateService } from "./services/bookmark-template.service";
import { BookmarksDialogComponent } from "./components/bookmark-templates/bookmarks-dialog/bookmarks-dialog.component";
import { DocumentTemplateDialogComponent } from "./components/bookmark-templates/document-template-dialog/document-template-dialog.component";
import { TemplateFormComponent } from "./components/cases-management/template-form/template-form.component";
import { CaseManagementService } from "./services/case-management.service";
import { DocumentTemplateFormValueService } from "./services/document-template-form-value.service"
import { ReasonComponent } from "./components/reason/reason.component";
import { ReasonDialogComponent } from "./components/reason/reason-dialog/reason-dialog.component";
import { ReasonService } from "./services/reason.service";
import { EFulfillmentFormComponent } from "./components/products/e-fulfillment-form/e-fulfillment-form.component";
import { ProductService } from "./services/product.service";
import { EfulfillmentService } from "./services/efulfillment.service";
import { CasesManagementDashboardComponent } from "./components/cases-management-dashboard/cases-management-dashboard.component";
import { FileDetailComponent } from "./components/file-detail/file-detail.component";
import { ErrorDetailComponent } from "./components/error-detail/error-detail.component";
import { DatePipe } from '@angular/common';
import { MemberLogsComponent } from "./components/member-logs/member-logs.component";

@NgModule({
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        HttpClientModule,
        HttpModule,
        FormsModule,
        ReactiveFormsModule,
        AppRoutingModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useClass: TranslateLanguageLoader
            }
        }),
        NgxDatatableModule,
        TooltipModule.forRoot(),
        PopoverModule.forRoot(),
        BsDropdownModule.forRoot(),
        CarouselModule.forRoot(),

        ChartsModule,
        NgxSpinnerModule,
        ModalModule.forRoot(),
        SnotifyModule,

        //ng-pipes
        //Material modules
        MatTooltipModule, MatIconModule, MatButtonModule, MatDialogModule, MatOptionModule, MatSelectModule, MatInputModule, MatProgressSpinnerModule,
        MatDatepickerModule, MatNativeDateModule, MatCheckboxModule, MatAutocompleteModule, MatBottomSheetModule, MatStepperModule, MatSlideToggleModule,
        MatTabsModule, MatListModule, MatMenuModule, MatCardModule, MatExpansionModule, MatProgressBarModule, MatGridListModule,
        MatPaginatorModule, MatTableModule
    ],
    declarations: [
        AppComponent,
        LoginComponent,
        HomeComponent,
        GroupsComponent,
        ProductsComponent, ProductDetailComponent,
        BenefitsComponent, VendorsComponent, MembersComponent, NewMemberComponent, HistoryComponent, MemberLogsComponent,
        CasesComponent, CaseDetailComponent,NewCaseComponent,
        SettingsComponent, QuoteSettingsComponent, 
        UsersManagementComponent, UserInfoComponent, UserPreferencesComponent,
        RolesManagementComponent, RoleEditorComponent, PaytypesComponent, WhitelabelsComponent, LoggersComponent,DependentEventlogDialogComponent,
        ServicesComponent, ContractTemplatesComponent, QuoteTemplatesComponent, QuoteHomeComponent, OrgsComponent, CustomersComponent, 
        NotFoundComponent,
        NotificationsViewerComponent,
        SearchBoxComponent,
        EqualValidator,
        LastElementDirective,
        AutofocusDirective,
        BootstrapTabDirective,
        BootstrapToggleDirective,
        BootstrapSelectDirective,
        BootstrapDatepickerDirective, DragDropDirective,
        GroupByPipe, SystemLogPipe, FilterPipe,
        CreateEditBenefitModalComponent, CreateEditVendorModalComponent, CreateEditProductModalComponent, WhitelabelConfigsComponent,
        CreateEditGroupModalComponent, CreateEditPaytypeModalComponent, CreateEditWhitelabelModalComponent ,LinkBenefitModalComponent, VendorProductLookupModalComponent, GroupProductModalComponent,
        PageHeaderComponent, MemberDetailDialogComponent, EditMemberSheetComponent, ChangePasswordSheetComponent, EditDefaultSheetComponent, LearnMoreContentSheetComponent, BenefitContentsComponent, BenefitIconSheetComponent,
        CreateEditServiceModalComponent, CreateEditContractTemplateModalComponent, CreateEditQuoteTemplateModalComponent, CreateEditQuoteModalComponent,
        CreateEditOrgModalComponent, ViewQuoteModalComponent,
        CreateEditCustomerModalComponent, MenuItemComponent, MenuItemContractComponent,

        BookmarkTemplatesComponent, BookmarksDialogComponent, DocumentTemplateDialogComponent, TemplateFormComponent,
        ReasonComponent, ReasonDialogComponent, EFulfillmentFormComponent, CasesManagementDashboardComponent,
        FileDetailComponent, ErrorDetailComponent
    ],
    providers: [
        { provide: 'BASE_URL', useFactory: getBaseUrl },
        { provide: ErrorHandler, useClass: AppErrorHandler },
        AlertService,
        ConfigurationService,
        AppTitleService,
        AppTranslationService,
        NotificationService,
        NotificationEndpoint,
        AccountService,
        AccountEndpoint,
        LocalStoreManager,
        EndpointFactory,
        ApiService,
        BsModalService,
        DependentService, BookmarkTemplateService, DocumentTemplateFormValueService,
        { provide: 'SnotifyToastConfig', useValue: ToastDefaults },
        SnotifyService, CaseManagementService,
        ReasonService, ProductService, EfulfillmentService,
        DatePipe
    ],
    entryComponents: [CreateEditBenefitModalComponent, CreateEditVendorModalComponent,
        CreateEditProductModalComponent, CreateEditGroupModalComponent, CreateEditPaytypeModalComponent, CreateEditWhitelabelModalComponent, WhitelabelConfigsComponent,
        LinkBenefitModalComponent, VendorProductLookupModalComponent, GroupProductModalComponent, MemberDetailDialogComponent,
        EditMemberSheetComponent, ChangePasswordSheetComponent, EditDefaultSheetComponent, LearnMoreContentSheetComponent, BenefitIconSheetComponent,
        CreateEditServiceModalComponent, CreateEditContractTemplateModalComponent, CreateEditQuoteTemplateModalComponent, CreateEditQuoteModalComponent,
        CreateEditOrgModalComponent, ViewQuoteModalComponent, 
        CreateEditCustomerModalComponent, DependentEventlogDialogComponent, BookmarksDialogComponent, DocumentTemplateDialogComponent,
        TemplateFormComponent, ReasonDialogComponent, EFulfillmentFormComponent
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
}




export function getBaseUrl() {
    return document.getElementsByTagName('base')[0].href;
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./ClientApp/app/app.module.ts